import type {
  InAppNotification,
  InAppNotificationApiRecord,
  InAppNotificationSocketPayload,
} from '@/types/inAppNotification';

/**
 * `id` is a JSON number over the socket and a string in a JSON:API `data.id`.
 * A malformed value must be rejected rather than coerced: `Number('abc')` is
 * `NaN`, and `NaN !== NaN` would defeat de-duplication forever while still
 * incrementing the unseen count on every re-delivery.
 */
const toId = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null;
  }

  const id = Number(value);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const toText = (value: unknown): string | null => {
  return typeof value === 'string' ? value : null;
};

const toReferenceId = (value: unknown): number | null => {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : null;
};

/**
 * Maps a `notification` socket frame onto the rendered shape.
 *
 * Returns `null` for anything unusable so a single malformed broadcast cannot
 * poison the cache. Non-string `title`/`body` are dropped rather than coerced —
 * they are rendered as text and must never be an object React tries to render.
 */
export const normaliseSocketNotification = (
  payload: InAppNotificationSocketPayload,
): InAppNotification | null => {
  const id = toId(payload?.id);

  if (id === null) {
    return null;
  }

  return {
    id,
    title: toText(payload.title),
    body: toText(payload.body),
    url: toText(payload.url),
    referenceType: toText(payload.reference_type),
    referenceId: toReferenceId(payload.reference_id),
    createdAt: toText(payload.created_at),
    seenAt: toText(payload.seen_at),
  };
};

/** Maps a JSON:API record onto the same shape the socket produces. */
export const normaliseApiNotification = (
  record: InAppNotificationApiRecord,
): InAppNotification | null => {
  const id = toId(record?.id);

  if (id === null) {
    return null;
  }

  const attributes = record.attributes ?? {};

  return {
    id,
    title: toText(attributes.title),
    body: toText(attributes.body),
    url: toText(attributes.url),
    referenceType: toText(attributes.referenceType),
    referenceId: toReferenceId(attributes.referenceId),
    createdAt: toText(attributes.createdAt),
    seenAt: toText(attributes.seenAt),
  };
};
